import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, MapPin, Calendar as CalendarIcon } from "lucide-react";

const MONTHS = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const WEEK = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

export default function PartnerCalendar({ endpoint = "/editions", base = "/partner/corsi" }) {
  const [editions, setEditions] = useState([]);
  const [current, setCurrent] = useState(new Date());

  useEffect(() => { api.get(endpoint).then(r => setEditions(r.data)); }, [endpoint]);

  const grid = useMemo(() => {
    const y = current.getFullYear(), m = current.getMonth();
    const first = new Date(y, m, 1);
    const firstIdx = (first.getDay() + 6) % 7; // Mon-first
    const days = new Date(y, m + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < firstIdx; i++) cells.push(null);
    for (let d = 1; d <= days; d++) cells.push(new Date(y, m, d));
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [current]);

  const eventsByDay = useMemo(() => {
    const map = {};
    editions.forEach((e) => {
      const k = e.start_date;
      (map[k] ||= []).push(e);
    });
    return map;
  }, [editions]);

  return (
    <div className="space-y-6" data-testid="calendar-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold text-slate-900">Calendario formativo</h1>
          <p className="text-slate-500 mt-1">Edizioni in aula e in videoconferenza.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => setCurrent(new Date(current.getFullYear(), current.getMonth() - 1, 1))} data-testid="cal-prev"><ChevronLeft className="w-4 h-4" /></Button>
          <div className="font-display font-bold text-lg min-w-[180px] text-center" data-testid="cal-title">{MONTHS[current.getMonth()]} {current.getFullYear()}</div>
          <Button variant="outline" size="icon" onClick={() => setCurrent(new Date(current.getFullYear(), current.getMonth() + 1, 1))} data-testid="cal-next"><ChevronRight className="w-4 h-4" /></Button>
        </div>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="grid grid-cols-7 text-xs font-bold uppercase tracking-widest text-slate-500 border-b border-slate-200 pb-2">
            {WEEK.map((w) => <div key={w} className="px-2">{w}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-px bg-slate-100 mt-2 rounded-lg overflow-hidden">
            {grid.map((d, i) => {
              const key = d?.toISOString().slice(0, 10);
              const evs = key ? eventsByDay[key] || [] : [];
              return (
                <div key={i} className={`min-h-[92px] bg-white p-2 ${!d ? "opacity-40" : ""}`}>
                  <div className="text-xs font-semibold text-slate-500">{d?.getDate()}</div>
                  {evs.map((e) => (
                    <Link
                      key={e.id}
                      to={`${base}/${e.course_id}`}
                      className="block mt-1 text-xs px-2 py-1 rounded bg-amber-100 text-amber-800 font-medium truncate hover:bg-amber-200 transition-colors"
                      title={`Apri scheda corso · ${e.course_title}`}
                      data-testid={`cal-event-${e.id}`}
                    >
                      {e.start_time} · {e.course_title}
                    </Link>
                  ))}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardContent className="p-6">
          <h2 className="font-display text-xl font-bold mb-4">Elenco edizioni</h2>
          <div className="divide-y divide-slate-100">
            {editions.length === 0 && <div className="text-slate-500 text-sm py-4">Nessuna edizione programmata.</div>}
            {editions.map((e) => (
              <div key={e.id} className="py-3 flex items-center gap-4" data-testid={`edition-item-${e.id}`}>
                <Link to={`${base}/${e.course_id}`} className="text-center bg-slate-50 rounded-lg px-3 py-2 min-w-[64px] hover:bg-amber-50 transition-colors" title="Apri scheda corso">
                  <div className="text-xs uppercase text-amber-700 font-bold">{new Date(e.start_date).toLocaleDateString("it-IT", { month: "short" })}</div>
                  <div className="font-display text-xl font-extrabold text-slate-900">{new Date(e.start_date).getDate()}</div>
                </Link>
                <div className="flex-1 min-w-0">
                  <Link to={`${base}/${e.course_id}`} className="font-semibold text-slate-900 hover:text-amber-700 hover:underline" data-testid={`edition-course-link-${e.id}`}>{e.course_title}</Link>
                  <div className="text-xs text-slate-500 flex items-center gap-2 flex-wrap">
                    <span>{e.start_time}</span> · <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {e.location || "-"}</span> · {e.modality}
                  </div>
                </div>
                {e.calendar_url && (
                  <a
                    href={e.calendar_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm font-semibold text-sky-700 hover:text-sky-900 hover:underline"
                    data-testid={`edition-calendar-link-${e.id}`}
                  >
                    <CalendarIcon className="w-4 h-4" /> Calendario
                  </a>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
